/**
 * Payment Error Component
 * Displays error information and recovery options
 */

import { Link } from 'react-router-dom';

interface PaymentErrorProps {
  errorCode?: string;
  errorMessage?: string;
  onRetry?: () => void;
  onContactSupport?: () => void;
}

export function PaymentError({
  errorCode = 'PAYMENT_FAILED',
  errorMessage = 'Your payment could not be processed',
  onRetry,
  onContactSupport,
}: PaymentErrorProps) {
  const errorDetails: Record<string, { title: string; description: string; suggestion: string }> = {
    CARD_DECLINED: {
      title: 'Cartão Recusado',
      description: 'Seu banco recusou a transação.',
      suggestion: 'Verifique os dados do cartão ou tente outro método de pagamento.',
    },
    INSUFFICIENT_FUNDS: {
      title: 'Fundos Insuficientes',
      description: 'Sua conta não tem saldo suficiente.',
      suggestion: 'Adicione fundos à sua conta e tente novamente.',
    },
    CARD_EXPIRED: {
      title: 'Cartão Expirado',
      description: 'O cartão fornecido está expirado.',
      suggestion: 'Use um cartão válido ou adicione um novo método de pagamento.',
    },
    INVALID_AMOUNT: {
      title: 'Valor Inválido',
      description: 'O valor da transação é inválido.',
      suggestion: 'Verifique o plano selecionado e tente novamente.',
    },
    PROCESSING_ERROR: {
      title: 'Erro de Processamento',
      description: 'Ocorreu um erro ao processar seu pagamento.',
      suggestion: 'Por favor, tente novamente ou entre em contato com nosso suporte.',
    },
    PAYMENT_FAILED: {
      title: 'Pagamento Não Realizado',
      description: errorMessage,
      suggestion: 'Tente novamente ou escolha outro método de pagamento.',
    },
  };

  const error = errorDetails[errorCode] || errorDetails.PAYMENT_FAILED;

  return (
    <div className="space-y-8">
      {/* Error Icon */}
      <div className="flex justify-center mb-8">
        <div className="relative w-24 h-24 flex items-center justify-center">
          <div className="absolute w-24 h-24 bg-red-500/20 rounded-full animate-pulse" />
          <div className="relative bg-red-500/10 border-2 border-red-500 rounded-full w-20 h-20 flex items-center justify-center">
            <svg
              className="w-10 h-10 text-red-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Error Message */}
      <div className="text-center space-y-3">
        <h2 className="text-3xl font-bold text-white">{error.title}</h2>
        <p className="text-white/60 text-lg">{error.description}</p>
      </div>

      {/* Error Details */}
      <div className="bg-[#111111] border border-white/5 rounded-2xl p-8 space-y-6">
        <div className="space-y-4">
          <h3 className="text-white font-semibold text-sm">O que você pode fazer:</h3>
          <ul className="space-y-2">
            <li className="flex items-start gap-3">
              <span className="text-[#C8A96E] mt-1">→</span>
              <span className="text-white/70">{error.suggestion}</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#C8A96E] mt-1">→</span>
              <span className="text-white/70">
                Certifique-se de que seu navegador permite pagamentos online.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#C8A96E] mt-1">→</span>
              <span className="text-white/70">
                Limpe os dados do seu navegador e tente novamente.
              </span>
            </li>
          </ul>
        </div>

        {/* Info Message */}
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
          <p className="text-sm text-yellow-300/80">
            ⚠️ Nenhuma cobrança foi realizada enquanto seu pagamento estava sendo processado.
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 pt-4">
        {onRetry ? (
          <button
            onClick={onRetry}
            className="flex-1 py-3 px-4 bg-[#C8A96E] text-[#0A0A0F] rounded-lg font-semibold hover:bg-[#D4B882] transition-colors"
          >
            Tentar Novamente
          </button>
        ) : (
          <Link
            to="/checkout"
            className="flex-1 py-3 px-4 bg-[#C8A96E] text-[#0A0A0F] rounded-lg font-semibold hover:bg-[#D4B882] transition-colors text-center"
          >
            Voltar para Checkout
          </Link>
        )}
        <button
          onClick={onContactSupport}
          className="flex-1 py-3 px-4 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-lg font-semibold transition-colors"
        >
          Contatar Suporte
        </button>
      </div>

      {/* Additional Help */}
      <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-4 space-y-2">
        <p className="text-sm text-blue-300/90 font-semibold">Precisa de ajuda?</p>
        <p className="text-sm text-blue-300/70">
          Se o problema persisti, visite nosso{' '}
          <Link to="/support" className="text-[#C8A96E] hover:underline">
            centro de ajuda
          </Link>
          {' '}ou entre em contato com{' '}
          <a
            href="mailto:irawildaws@gmail.com"
            className="text-[#C8A96E] hover:underline"
          >
            irawildaws@gmail.com
          </a>
        </p>
      </div>
    </div>
  );
}
